/**
 * Builds paste-ready Iris CLI commands for launching JSMA in a simulator
 * with the current `/test/rbs` configuration.
 *
 * Shape matches local MAP `react-native/README.md` ("Using Iris CLI"):
 *   iris run android:jsma --account=new --locale="en-US" --proctor="..."
 *   iris run ios:jsma --account=new --proctor="..."
 * That section links to
 * https://code.corp.indeed.com/mobile-app-platform/iris#iris-cli
 */

export type IrisSimulatorPlatform = 'ios' | 'android';

export interface IrisSimulatorCommandConfig {
    platform: IrisSimulatorPlatform;
    locale: string;
    proctorGroups: string;
    accountEmail: string;
    env?: string;
}

/** QA environment Iris accepts besides the default `qa`. */
export const IRIS_SIMULATOR_ENV = 'e2eqa';

const APP_TARGET: Record<IrisSimulatorPlatform, string> = {
    ios: 'ios:jsma',
    android: 'android:jsma',
};

const SHELL_UNSAFE = /[\s"'\\$`;&|<>()]/;

/** Quote a flag value so the pasted command is one shell word. */
export const quoteIrisFlagValue = (value: string): string =>
    `"${value
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\$/g, '\\$')
        .replace(/`/g, '\\`')}"`;

const formatUnquotedOrQuoted = (value: string): string =>
    SHELL_UNSAFE.test(value) ? quoteIrisFlagValue(value) : value;

/** `--flag=value`, quoting only when the shell needs it (MAP `--account=new`). */
const formatEqualsOption = (name: string, value: string): string =>
    `--${name}=${formatUnquotedOrQuoted(value)}`;

/**
 * Network-dump filename derived from the account local-part
 * (`test-rbs-b4135156@mailosaur.io` → `test-rbs-b4135156.jsonl`).
 * Returns `null` when the local-part is empty after sanitizing.
 * Iris requires this path to end in `.jsonl` (experimental, app only).
 */
export const networkDumpFilename = (accountEmail: string): string | null => {
    const localPart = accountEmail.trim().split('@')[0] ?? '';
    const safe = localPart.replace(/[^A-Za-z0-9._-]/g, '_');
    return safe ? `${safe}.jsonl` : null;
};

/**
 * One complete command, joined with ` \` newlines so it pastes into zsh/bash
 * without reformatting. Target comes first, then flags, as in MAP RN examples.
 */
export const buildIrisSimulatorCommand = ({
    platform,
    locale,
    proctorGroups,
    accountEmail,
    env = IRIS_SIMULATOR_ENV,
}: IrisSimulatorCommandConfig): string => {
    const parts = [`iris run ${APP_TARGET[platform]}`];

    const trimmedAccount = accountEmail.trim();
    if (trimmedAccount) {
        parts.push(formatEqualsOption('account', trimmedAccount));
    }

    const trimmedLocale = locale.trim();
    if (trimmedLocale) {
        parts.push(`--locale=${quoteIrisFlagValue(trimmedLocale)}`);
    }

    const trimmedProctor = proctorGroups.trim();
    if (trimmedProctor) {
        parts.push(`--proctor=${quoteIrisFlagValue(trimmedProctor)}`);
    }

    if (trimmedAccount) {
        const dumpFile = networkDumpFilename(trimmedAccount);
        if (dumpFile) {
            parts.push(formatEqualsOption('dump-network', dumpFile));
        }
    }

    const trimmedEnv = env.trim();
    if (trimmedEnv) {
        parts.push(formatEqualsOption('env', trimmedEnv));
    }

    return parts.join(' \\\n');
};
